import { useMessage } from 'naive-ui'
import { useAgentChannelsStore, type AgentChannelSummary } from '../stores/agent_channels'

export function useChannelActions() {
  const store = useAgentChannelsStore()
  const toast = useMessage()

  async function run(action: () => Promise<unknown>, success?: string): Promise<boolean> {
    try {
      await action()
      if (success) toast.success(success)
      return true
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e))
      return false
    }
  }

  const create = (name: string) =>
    run(async () => {
      const channel = await store.createChannel(name)
      await store.select(channel.id)
    }, `Created #${name}`)

  const rename = (channel: AgentChannelSummary, name: string) =>
    run(() => store.updateChannel(channel.id, { name }), `Renamed to #${name}`)

  const setArchived = (channel: AgentChannelSummary, archived: boolean) =>
    run(() => store.updateChannel(channel.id, { archived }), archived ? 'Channel archived' : 'Channel unarchived')

  const remove = (channel: AgentChannelSummary) =>
    run(() => store.deleteChannel(channel.id), `Deleted #${channel.name}`)

  const addMember = (channel: AgentChannelSummary, threadId: string) =>
    run(() => store.addMember(channel.id, threadId), 'Member added')

  const removeMember = (channel: AgentChannelSummary, threadId: string) =>
    run(() => store.removeMember(channel.id, threadId), 'Member removed')

  const post = (channel: AgentChannelSummary, body: string, onWoken?: (count: number) => void) =>
    run(async () => {
      const woken = await store.postMessage(channel.id, body)
      onWoken?.(woken)
    })

  return { create, rename, setArchived, remove, addMember, removeMember, post }
}
